import type { ImgHTMLAttributes } from "react";

export type PictureSource = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  picture: PictureSource;
  sizes?: string;
  alt: string;
  eager?: boolean;
  priority?: boolean;
};

// Normalize "avif" / "webp" / "jpg" keys to MIME types; pass through "image/*" as-is.
function toMime(key: string): string {
  if (key.includes("/")) return key;
  if (key === "jpg" || key === "jpeg") return "image/jpeg";
  return `image/${key}`;
}

// AVIF first, WebP second, raster fallback last.
const PRIORITY: Record<string, number> = {
  "image/avif": 0,
  "image/webp": 1,
  "image/jpeg": 2,
  "image/png": 3,
};

export function ResponsiveImage({
  picture,
  sizes = "100vw",
  alt,
  eager = false,
  priority = false,
  width,
  height,
  ...rest
}: Props) {
  const { sources, img } = picture;
  const entries = Object.entries(sources)
    .map(([k, srcset]) => ({ mime: toMime(k), srcset }))
    .sort((a, b) => (PRIORITY[a.mime] ?? 99) - (PRIORITY[b.mime] ?? 99));
  // Last entry (raster) drives the <img> srcset fallback.
  const fallback = entries[entries.length - 1];
  const modern = entries.slice(0, -1);
  return (
    <picture>
      {modern.map(({ mime, srcset }) => (
        <source key={mime} type={mime} srcSet={srcset} sizes={sizes} />
      ))}
      <img
        src={img.src}
        srcSet={fallback?.srcset}
        sizes={sizes}
        width={width ?? img.w}
        height={height ?? img.h}
        alt={alt}
        loading={eager || priority ? "eager" : "lazy"}
        decoding="async"
        // @ts-expect-error — fetchpriority is valid HTML, React 19 supports it
        fetchpriority={priority ? "high" : undefined}
        {...rest}
      />
    </picture>
  );
}